import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Switch } from "@mui/base/Switch";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import dayjs from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  BoxButtons,
  CancelButton,
  PrimaryButton,
  MsgError,
  SwitchContainer,
  SwitchLabel,
} from "./styles";
import { DataAllocationToApi } from "../../../@types/dataAllocationToApi.type";
import {
  addOrUpdateProjectResourceAllocation,
  deleteProjectResourceAllocation,
  deleteProjectResourceAllocationByLinkIdResourceId,
  getProjectResourceAllocationById,
  hasBlockedDates,
  searchAllocations,
} from "../../../services/api/Allocations/AllocationsService";

import {
  getAllProjectsByCreatedUserIdAndManagerId,
  getAllProjectsIdsAndNames,
} from "../../../services/api/projects/projectService";
import { getAllResources } from "../../../services/api/resource/resourceService";
import { IKeyValue } from "../../../@types/key-value.type";
import { useConfirm } from "../../../hooks/useConfirm";
import { toast } from "react-toastify";
import { useAllocation } from "../../hooks/useAllocations";
import closeIconModal from "./../../../assets/_CloseIconModal_.svg";

import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import { storageUserGet } from "../../../storage/storageUser";
import { useCalendar } from "../../context/CalendarProvider";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { getAllUnavailability } from "../../../services/api/unavailability/unavailabilityService";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import styles from "./styles.module.scss";
import { IHoliday } from "../../../@types/holiday.type";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface ModalFormAlocationProps {
  dataAllocation: DataAllocationToApi;
  open: boolean;
  handleClose: () => void;
  isEdit?: boolean;
  wasOpenedByTheFilter?: boolean;
}

export const ModalFormAlocation = ({
  dataAllocation,
  open,
  handleClose,
  isEdit,
  wasOpenedByTheFilter,
}: ModalFormAlocationProps) => {
  const [othersInfoAllocation, setOthersInfoAllocation] = useState<{
    id: string;
    allocationPercentage: number;
    isMonthAllocation: boolean;
    linkId: string | null;
  }>({
    id: "",
    allocationPercentage: 0,
    isMonthAllocation: true,
    linkId: null,
  });
  const [totalHours, setTotalHours] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    "Indisponibilidade" | "Projeto" | "Projeção"
  >("Projeto");
  const [timeMode, setTimeMode] = useState(
    selectedOption === "Projeto" ? 1 : 0
  );
  const [unavailability, setUnavailability] = useState<IKeyValue[]>([]);
  const [project, setProject] = useState<IKeyValue[]>([]);
  const [resources, setResources] = useState<IKeyValue[]>([]);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const user = storageUserGet();

  const AllocationSchema = useMemo(() => z
    .object({
      resources: z
        .array(
          z.object({
            key: z.string(),
            value: z.string(),
          })
        )
        .min(1, "Selecione pelo menos um colaborador"),
      startDate: z.custom((value) => dayjs(value).isValid(), {
        message: "Data de início inválida",
      }),
      endDate: z.custom((value) => dayjs(value).isValid(), {
        message: "Data de término inválida",
      }),
      allocationTime: z
        .string()
        .optional()
        .transform((val) => val || "")
        .refine(
          (value) => {
            if (timeMode === 0) {
              return value.length > 0 && /^([1-9]|1[0-9]|2[0-4])$/.test(value);
            }
            return true;
          },
          {
            message: "Insira horas entre 1 e 24",
          }
        ),
      allocationTimePerMonth: z
        .string()
        .optional()
        .transform((val) => val || "")
        .refine(
          (value) => {
            if (timeMode === 1) {
              return value.length > 0 && Number(value) > 0;
            }
            return true;
          },
          {
            message: "Informe um valor válido",
          }
        ),
      projectOrUnavailability: z
        .string()
        .min(1, { message: "Campo obrigatório" }),
      billable: z.string().refine(
        (value) => {
          if (selectedOption === "Projeto") {
            return value.length > 0;
          }
          return true;
        },
        {
          message: "Campo obrigatório",
        }
      ),
      details: z.string().max(1000, "Esse campo deve ter até 1000 caracteres").refine(
        (value) => {
          if (selectedOption === "Projeção") {
            return !!value;
          }
          return true;
        },
        {
          message: "Detalhes da oportunidade são obrigatórios para projeção",
        }
      ),
      opportunityLink: z
        .string()
        .max(100, "Link deve ter até 100 caracteres")
        .refine(
          (value) => {
            if (selectedOption === "Projeção") {
              return !!value;
            }
            return true;
          },
          {
            message: "Link da oportunidade é obrigatório para projeção",
          }
        ),
    })
    .refine(
      (data) => dayjs(data.endDate).isSameOrAfter(dayjs(data.startDate)),
      {
        message: "Data de término inválida",
        path: ["endDate"],
      }
    ), [timeMode, selectedOption]);

  type TypeAllocationData = z.infer<typeof AllocationSchema>;

  const {
    clearErrors,
    unregister,
    register,
    setValue,
    watch,
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(AllocationSchema),
    defaultValues: {
      resources: [] as IKeyValue[],
      startDate: dayjs(dataAllocation.startDate),
      endDate: dayjs(dataAllocation.endDate),
      allocationTime: "",
      allocationTimePerMonth: "",
      projectOrUnavailability: "",
      billable: "",
      details: "",
      opportunityLink: "",
    },
  });
  const {
    setAllocations,
    allocations,
    searchResource,
    getNumberValueAndIdFromCategories,
    range,
  } = useAllocation();

  const resourceDismissedDate = useMemo(() => {
    if (!dataAllocation?.resourceId) return null;
    return allocations.find((a) => a.id === dataAllocation.resourceId)?.label.dismissedDate ?? null;
  }, [allocations, dataAllocation?.resourceId]);

  const { zoom, listHolidays } = useCalendar();

  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const [openBlockedDatesConfirm, setOpenBlockedDatesConfirm] = useState(false);

  const handleCloseBlockedDatesConfirm = () => {
    setOpenBlockedDatesConfirm(false);
  };

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o Apontamento?",
    openModalConfirm,
    handleCloseModalConfirm,
    "Após a exclusão não terá como recuperar ela novamente."
  );

  const {
    showConfirm: showBlockedDatesConfirm,
    Confirm: BlockedDatesConfirmModal,
  } = useConfirm(
    "Atenção",
    openBlockedDatesConfirm,
    handleCloseBlockedDatesConfirm,
    "Dentro desse período de alocação, algum dos colaboradores possuem períodos bloqueados. Ao confirmar, os bloqueios serão ignorados e a alocação será realizada. Deseja prosseguir?",
    true
  );


  const getIsHoliday = (date: dayjs.Dayjs, listHolidays: IHoliday[] = []) => {
    return listHolidays?.find((holiday) => {
      const holidayDate = dayjs(holiday.holidayDate);
      return date.isSame(holidayDate, "day");
    });
  };

  const isHoliday = useMemo(() => {
    return getIsHoliday(dayjs(watch("startDate")), listHolidays);
  }, [watch("startDate")]);

  const isProjectionDisabled = useMemo(() => {
    const startDate = dayjs(watch("startDate"));
    const today = dayjs().startOf('day');

    const isPastDate = startDate.isBefore(today);

    const isEditDisabled = isEdit && dataAllocation.isProject;

    return isPastDate || isEditDisabled;
  }, [watch("startDate"), isEdit, dataAllocation.isProject]);

  useEffect(() => {
    const startDate = dayjs(watch("startDate"));
    const today = dayjs().startOf('day');
    const isPastDate = startDate.isBefore(today);

    if (selectedOption === "Projeção" && isPastDate && !isEdit) {
      setSelectedOption("Projeto");
    }
  }, [watch("startDate"), selectedOption, isEdit]);

  useEffect(() => {
    if (!watch("startDate") || !watch("endDate")) return;

    const startDate = dayjs(watch("startDate"));
    const endDate = dayjs(watch("endDate"));
    let totalHours = 0;

    if (timeMode === 0) {
      const hoursPerDay = Number(watch("allocationTime")) || 0;
      let totalWorkDays = 0;

      for (
        let date = startDate;
        date.isSameOrBefore(endDate, "day");
        date = date.add(1, "day")
      ) {
        const isWeekend = date.day() === 0 || date.day() === 6;

        if (!isWeekend) {
          totalWorkDays++;
        }
      }

      totalHours = totalWorkDays * hoursPerDay;
    } else {
      const monthsDiff = endDate.diff(startDate, "month", true);
      const hoursPerMonth = watch("allocationTimePerMonth") || 0;
      totalHours = Math.round(monthsDiff * Number(hoursPerMonth));
    }

    setOthersInfoAllocation((prev) => ({
      ...prev,
      occupancy: totalHours,
    }));

    setTotalHours(totalHours);
  }, [
    watch("startDate"),
    watch("endDate"),
    watch("allocationTime"),
    watch("allocationTimePerMonth"),
    timeMode,
  ]);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const isAllocatable = true;
        const isDismissed = false;
        let responseResources;

        let permissions = usePermission(FeatureStatic.allocationMap.id);

        if (permissions.canEditMine()) {
          responseResources = await getAllResources(isAllocatable, isDismissed);
        } else if (permissions.canEdit()) {
          responseResources = await getAllResources(isAllocatable, isDismissed);
        }
        setResources(responseResources as IKeyValue[]);
      } catch (error) {
        console.log(error);
      }
    };

    const fetchProjectsOrUnavailability = async () => {
      try {
        let responseProjects;
        let responseUnavailability;

        let permissions = usePermission(FeatureStatic.allocationMap.id);

        if (permissions.canEditMine()) {
          responseProjects = (await getAllProjectsByCreatedUserIdAndManagerId(
            user.id,
            user.resourceId
          )) as IKeyValue[];

          if (responseProjects.length == 0)
            toast.error(`Você não tem projetos para o seu nivel de acesso`);
        } else if (permissions.canEdit()) {
          responseProjects = await getAllProjectsIdsAndNames();
        }
        setProject(responseProjects as IKeyValue[]);

        responseUnavailability = await getAllUnavailability();
        const mappedUnavailabilities = responseUnavailability.result.map(
          (item: any) => ({
            key: item.id,
            value: item.unavailabilityName,
          })
        );
        setUnavailability(mappedUnavailabilities as IKeyValue[]);
      } catch (error) {
        console.log(error);
      }
    };

    if (open) {
      if (project.length === 0 && unavailability.length === 0) {
        fetchProjectsOrUnavailability();
      }

      if (resources.length === 0) {
        fetchResources();
      }
    }
  }, [open]);

  useEffect(() => {
    const fetchProjectResourceAllocationById = async (id: string) => {
      try {
        const response: any = await getProjectResourceAllocationById(id);

        if (selectedOption) {
          let projectOrUnavailabilityValue = "";

          if (selectedOption === "Projeto") {
            projectOrUnavailabilityValue = response.result?.projectId || "";
          } else if (selectedOption === "Indisponibilidade") {
            projectOrUnavailabilityValue = response.result?.unavailabilityId || "";
          } else if (selectedOption === "Projeção") {
            projectOrUnavailabilityValue = response.result?.projectName || "";
          }
          console;
          reset({
            startDate: dayjs(response.result?.startDate),
            endDate: dayjs(response.result?.endDate),
            billable: response.result?.isBillable?.toString(),
            details: response.result?.details || "",
            opportunityLink: response.result?.opportunityLink || "",
            allocationTime: response.result?.allocationTime?.toString(),
            allocationTimePerMonth:
              response.result?.monthAllocationHours?.toString(),
            projectOrUnavailability: projectOrUnavailabilityValue,
            resources: response.result.resources.map(
              ({ id, name }: { id: string; name: string }) => ({
                key: id,
                value: name,
              })
            ),
          });
        }

        setOthersInfoAllocation({
          id: response.result?.id,
          allocationPercentage: response.result?.allocationPercentage,
          isMonthAllocation: response.result?.isMonthAllocation,
          linkId: response.result?.linkId,
        });

        setTimeMode(response.result?.isMonthAllocation ? 1 : 0);
      } catch (error) {
        console.log(error);
      }
    };

    if (open) {
      if (dataAllocation?.id) {
        const fetchAndSetType = async () => {
          try {
            const response: any = await getProjectResourceAllocationById(dataAllocation.id!);

            if (response.result?.isProjection) {
              setSelectedOption("Projeção");
            } else if (response.result?.projectId) {
              setSelectedOption("Projeto");
            } else if (response.result?.unavailabilityId) {
              setSelectedOption("Indisponibilidade");
            } else {
              setSelectedOption(
                dataAllocation.isProject ? "Projeto" : "Indisponibilidade"
              );
            }
          } catch (error) {
            console.log(error);
            setSelectedOption(
              dataAllocation.isProject ? "Projeto" : "Indisponibilidade"
            );
          }
        };

        fetchAndSetType();
        fetchProjectResourceAllocationById(dataAllocation?.id);
      } else {
        if (selectedOption === "Indisponibilidade") {
          setTimeMode(0);
        }

        const mappedResources = wasOpenedByTheFilter
          ? allocations.map(({ id, label }) => ({
            key: id,
            value: label.title,
          }))
          : [
            {
              key: dataAllocation?.resourceId as string,
              value: dataAllocation?.title,
            },
          ];

        reset({
          resources: mappedResources,
          startDate: dayjs(dataAllocation.startDate),
          endDate: dayjs(dataAllocation.startDate)?.add(1, "month"),
          allocationTime: selectedOption == "Projeto" ? "" : "8",
          allocationTimePerMonth: "",
          projectOrUnavailability: "",
          billable: dataAllocation.isProject ? "true" : "false",
          details: "",
          opportunityLink: "",
        });

        setOthersInfoAllocation({
          ...othersInfoAllocation,
        });
      }
    }
  }, [open, selectedOption, dataAllocation, reset, wasOpenedByTheFilter]);

  const handleDeleteClick = async (id: string) => {
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        setIsLoading(true);
        let newList;
        let response;
        if (othersInfoAllocation?.linkId) {
          response = await deleteProjectResourceAllocationByLinkIdResourceId(
            othersInfoAllocation?.linkId
          );
          newList = allocations.map((item) => ({
            ...item,
            data: item.data.filter(
              (dataItem) => dataItem.linkId !== othersInfoAllocation?.linkId
            ),
          }));
        } else {
          response = await deleteProjectResourceAllocation(id);
          newList = allocations.map((item) => ({
            ...item,
            data: item.data.filter((dataItem) => dataItem.id !== id),
          }));
        }

        console.log("Response da exclusão:", response);

        if (response.status === 200 || response.statusCode === 200) {
          setAllocations(newList);
          toast.success("Apontamento excluído com sucesso!");
          handleClose();
        } else if (response.status === 412 || response.statusCode === 412) {
          toast.error(
            "Não foi possível excluir o apontamento! O registro possui dependências ou foi modificado por outro usuário."
          );
        } else {
          toast.error(
            "Não foi possível excluir a apontamento! Tente novamente mais tarde."
          );
        }
      } catch (error) {
        toast.error(
          "Não foi possível excluir a apontamento! Tente novamente mais tarde."
        );
        console.error("Erro ao excluir:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (timeMode === 0) {
      register("allocationTime", { required: "Campo obrigatório" });
      unregister("allocationTimePerMonth");
      clearErrors("allocationTimePerMonth");
      setValue("allocationTimePerMonth", "");
    } else {
      register("allocationTimePerMonth", { required: "Campo obrigatório" });
      unregister("allocationTime");
      clearErrors("allocationTime");
      setValue("allocationTime", "");
    }
  }, [timeMode, register, unregister, clearErrors, setValue]);

  const onSubmit = async (data: TypeAllocationData) => {
    let infoDataToApi;
    const resourcesMapedFromApi = data.resources.map((resource) => {
      return {
        id: resource.key,
        name: resource.value,
      };
    });

    const isProjection = selectedOption === "Projeção";

    if (othersInfoAllocation.id !== "") {
      infoDataToApi = {
        id: othersInfoAllocation?.id,
        projectId:
          selectedOption === "Projeto" ? data.projectOrUnavailability : null,
        unavailabilityId:
          selectedOption === "Indisponibilidade"
            ? data.projectOrUnavailability
            : null,
        projectName:
          selectedOption === "Projeção" ? data.projectOrUnavailability : null,
        startDate:
          timeMode === 0
            ? dayjs(data.startDate).startOf("day").toDate().toISOString()
            : dayjs(data.startDate).startOf("month").toDate().toISOString(),
        endDate:
          timeMode === 0
            ? dayjs(data.endDate).startOf("day").toDate().toISOString()
            : dayjs(data.endDate)
              .endOf("month")
              .startOf("day")
              .toDate()
              .toISOString(),
        allocationTime: timeMode === 0 ? Number(data.allocationTime) : 0,
        monthAllocationHours: timeMode === 1 ? Number(data.allocationTimePerMonth) : 0,
        isMonthAllocation: timeMode === 1,
        allocationPercentage: othersInfoAllocation?.allocationPercentage,
        isBillable:
          selectedOption === "Projeto" && data.billable === "true"
            ? true
            : false,
        resources: resourcesMapedFromApi,
        linkId: othersInfoAllocation.linkId,
        details: data.details,
        isProjection: isProjection,
        opportunityLink: data.opportunityLink || "",
      };
    } else {
      infoDataToApi = {
        projectId:
          selectedOption === "Projeto" ? data.projectOrUnavailability : null,
        unavailabilityId:
          selectedOption === "Indisponibilidade"
            ? data.projectOrUnavailability
            : null,
        projectName:
          selectedOption === "Projeção" ? data.projectOrUnavailability : null,
        startDate:
          timeMode === 0
            ? dayjs(data.startDate).startOf("day").toDate().toISOString()
            : dayjs(data.startDate).startOf("month").toDate().toISOString(),
        endDate:
          timeMode === 0
            ? dayjs(data.endDate).startOf("day").toDate().toISOString()
            : dayjs(data.endDate)
              .endOf("month")
              .startOf("day")
              .toDate()
              .toISOString(),
        allocationTime: timeMode === 0 ? Number(data.allocationTime) : 0,
        monthAllocationHours: timeMode === 1 ? Number(data.allocationTimePerMonth) : 0,
        isMonthAllocation: timeMode === 1,
        isBillable:
          selectedOption === "Projeto" && data.billable === "true"
            ? true
            : false,
        resources: resourcesMapedFromApi,
        linkId: othersInfoAllocation.linkId,
        details: data.details,
        isProjection: isProjection,
        opportunityLink: data.opportunityLink || "",
      };
    }

    try {
      setIsLoading(true);
      let ignoreBlocks = false;

      const hasBlockedDatesResponse = await hasBlockedDates({
        resources: resourcesMapedFromApi,
        startDate: infoDataToApi.startDate,
        endDate: infoDataToApi.endDate,
      });

      if (hasBlockedDatesResponse.result == "true") {
        const confirm = await showBlockedDatesConfirm();
        if (!confirm) {
          setIsLoading(false);
          return;
        }
        ignoreBlocks = true;
      }

      const responseAddOrUpdate = await addOrUpdateProjectResourceAllocation({
        ...infoDataToApi,
        ignoreBlocks,
      });

      if (responseAddOrUpdate.statusCode === 200) {
        const { idCategorieArray, numberValueCategories } =
          getNumberValueAndIdFromCategories();
        let endDateToApi;
        switch (zoom) {
          case 1:
            endDateToApi = dayjs(range.startDate)
              .endOf("month")
              .startOf("day")
              .toDate();
            break;
          case 0:
            endDateToApi = dayjs(range.startDate)
              .add(2, "month")
              .endOf("month")
              .startOf("day")
              .toDate();
            break;
          case 3:
            endDateToApi = dayjs(range.startDate)
              .add(5, "month")
              .endOf("month")
              .startOf("day")
              .toDate();
            break;
          case 4:
            endDateToApi = dayjs(range.startDate)
              .add(11, "month")
              .endOf("month")
              .startOf("day")
              .toDate();
            break;
          default:
            endDateToApi = dayjs(range.startDate)
              .add(4, "month")
              .endOf("month")
              .startOf("day")
              .toDate();
            break;
        }
        const response = await searchAllocations(
          0,
          500,
          numberValueCategories,
          searchResource,
          idCategorieArray?.map((item) => item.key),
          dayjs(range.startDate).startOf("month").toDate(),
          endDateToApi
        );
        setAllocations(response.result.items);
        handleClose();
        toast.success(`Apontamento salvo com sucesso!`);
      }
    } catch (error) {
      toast.error(
        `Não foi possível salvar o apontamento! Tente novamente mais tarde.`
      );
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeModeChange = (event: any) => {
    const newTimeMode = event.target.checked ? 1 : 0;
    reset((items) => {
      return {
        ...items,
        allocationTime: "",
        allocationTimePerMonth: "",
      };
    });
    setTimeMode(newTimeMode);
  };

  const handleOptionChange = (
    _event: ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const typedValue = value as "Indisponibilidade" | "Projeto" | "Projeção";
    setSelectedOption(typedValue);

    if (typedValue === "Projeto" || typedValue === "Projeção") {
      setTimeMode(1);
    } else {
      setTimeMode(0);
    }
  };

  const handleModalClose = () => {
    setSelectedOption("Projeto");
    handleClose();
  };

  const handleConvertToAllocation = async () => {
    if (selectedOption !== "Projeção" || !othersInfoAllocation.id) return;

    try {
      setIsLoading(true);

      const currentData = watch();

      const selectedProject = project.find(p =>
        p.value === currentData.projectOrUnavailability ||
        p.key === currentData.projectOrUnavailability
      );

      if (!selectedProject) {
        toast.error(
          "Para converter em alocação, é necessário selecionar um projeto que já esteja cadastrado no sistema."
        );
        setIsLoading(false);
        return;
      }

      const conversionData = {
        id: othersInfoAllocation.id,
        projectId: selectedProject.key,
        unavailabilityId: null,
        projectName: null,
        startDate: (() => {
          if (timeMode === 0) {
            return dayjs(currentData.startDate).startOf("day").toDate().toISOString();
          } else {
            return dayjs(currentData.startDate).startOf("month").toDate().toISOString();
          }
        })(),
        endDate: (() => {
          if (timeMode === 0) {
            return dayjs(currentData.endDate).startOf("day").toDate().toISOString();
          } else {
            return dayjs(currentData.endDate).endOf("month").startOf("day").toDate().toISOString();
          }
        })(),
        allocationTime: timeMode === 0 ? Number(currentData.allocationTime) : 0,
        monthAllocationHours: timeMode === 1 ? Number(currentData.allocationTimePerMonth) : 0,
        isMonthAllocation: timeMode === 1,
        allocationPercentage: othersInfoAllocation?.allocationPercentage,
        isBillable: currentData.billable === "true" ? true : false,
        resources: currentData.resources.map((resource) => ({
          id: resource.key,
          name: resource.value,
        })),
        linkId: othersInfoAllocation.linkId,
        details: currentData.details,
        isProjection: false,
        opportunityLink: currentData.opportunityLink || "",
      };

      const responseUpdate = await addOrUpdateProjectResourceAllocation(conversionData);

      if (responseUpdate.statusCode === 200) {
        const { idCategorieArray, numberValueCategories } = getNumberValueAndIdFromCategories();
        let endDateToApi;

        switch (zoom) {
          case 1:
            endDateToApi = dayjs(range.startDate).endOf("month").startOf("day").toDate();
            break;
          case 0:
            endDateToApi = dayjs(range.startDate).add(2, "month").endOf("month").startOf("day").toDate();
            break;
          case 3:
            endDateToApi = dayjs(range.startDate).add(5, "month").endOf("month").startOf("day").toDate();
            break;
          case 4:
            endDateToApi = dayjs(range.startDate).add(11, "month").endOf("month").startOf("day").toDate();
            break;
          default:
            endDateToApi = dayjs(range.startDate).add(4, "month").endOf("month").startOf("day").toDate();
            break;
        }

        const response = await searchAllocations(
          0,
          500,
          numberValueCategories,
          searchResource,
          idCategorieArray?.map((item) => item.key),
          dayjs(range.startDate).startOf("month").toDate(),
          endDateToApi
        );

        setAllocations(response.result.items);
        handleClose();
        toast.success("Projeção convertida em alocação com sucesso!");
      } else {
        toast.error("Não foi possível converter a projeção em alocação!");
      }
    } catch (error) {
      toast.error("Erro ao converter projeção em alocação!");
      console.error("Erro:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {Confirm}
      {BlockedDatesConfirmModal}
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <div className={styles.modalBox}>
          <div className={styles.modalTitle}>
            <h3>Apontar Colaborador</h3>
            {isHoliday && (
              <span style={{ color: "#ff1e1e" }}>
                Período com feriado! ({isHoliday.name})
              </span>
            )}
            {resourceDismissedDate && (
              <span style={{ color: "#ff1e1e" }}>
                Colaborador desligado em {dayjs(resourceDismissedDate).format("DD/MM/YYYY")}
              </span>
            )}

            <Button onClick={handleClose} className={styles.iconButton}>
              {closeIconModal && <img src={closeIconModal} />}
            </Button>
          </div>
          <div className={styles.containerModal}>
            <FormControl component="fieldset" style={{ marginBottom: "16px" }}>
              <div className={styles.modalText}>Tipo de apontamento</div>
              <RadioGroup
                row
                name="allocationType"
                value={selectedOption}
                onChange={handleOptionChange}
              >
                <FormControlLabel
                  value="Projeto"
                  control={<Radio />}
                  label="Alocação"
                  disabled={isEdit && dataAllocation.isProject}
                />
                <FormControlLabel
                  value="Indisponibilidade"
                  control={<Radio />}
                  label="Indisponibilidade"
                  disabled={isEdit && dataAllocation.isProject}
                />
                <FormControlLabel
                  value="Projeção"
                  control={<Radio />}
                  label="Projeção"
                  disabled={isProjectionDisabled}
                />
              </RadioGroup>
            </FormControl>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.resourcesModal}>
                <Controller
                  name="resources"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      limitTags={3}
                      fullWidth
                      id="checkboxes-tags-demo"
                      options={resources}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option.value}
                      isOptionEqualToValue={(option, value) =>
                        option.key === value.key
                      }
                      noOptionsText="Sem opções"
                      renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props;

                        return (
                          <li key={option.key} {...optionProps}>
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.value}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Colaboradores"
                          placeholder="Selecione"
                          error={!!errors.resources}
                          helperText={errors.resources?.message}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      )}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                    />
                  )}
                />
              </div>

              <div>
                {(selectedOption === "Projeto" || selectedOption === "Projeção") && (
                  <div className={styles.switchModal}>
                    <SwitchContainer>
                      <SwitchLabel
                        activeLabel={timeMode === 0 ? true : undefined}
                      >
                        Horas por dia
                      </SwitchLabel>
                      <Switch
                        className={styles.switchRoot}
                        checked={timeMode === 1}
                        onChange={handleTimeModeChange}
                      />
                      <SwitchLabel
                        activeLabel={timeMode === 1 ? true : undefined}
                      >
                        Horas por mês
                      </SwitchLabel>
                    </SwitchContainer>
                  </div>
                )}
                <div className={styles.datesModal}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field }) => (
                      <div
                        style={{
                          flex: "1",
                        }}
                      >
                        <DatePicker
                          label="Data Inicial"
                          views={
                            timeMode === 0
                              ? ["year", "month", "day"]
                              : ["year", "month"]
                          }
                          value={field.value ? field.value : null}
                          maxDate={resourceDismissedDate ? dayjs(resourceDismissedDate) : undefined}
                          onChange={(e) => {
                            field.onChange(e);
                          }}
                        />
                        <div style={{ height: "20px", marginTop: "4px" }}>
                          {errors.startDate && (
                            <MsgError>{errors.startDate.message}</MsgError>
                          )}
                        </div>
                      </div>
                    )}
                  />
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => {
                      return (
                        <div
                          style={{
                            flex: "1",
                          }}
                        >
                          <DatePicker
                            label="Data Final"
                            value={field.value}
                            views={
                              timeMode === 0
                                ? ["year", "month", "day"]
                                : ["year", "month"]
                            }
                            maxDate={resourceDismissedDate ? dayjs(resourceDismissedDate) : undefined}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                          />
                          <div style={{ height: "20px", marginTop: "4px" }}>
                            {errors.endDate && (
                              <MsgError>{errors.endDate.message}</MsgError>
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />

                  {timeMode === 0 ? (
                    <Controller
                      name="allocationTime"
                      control={control}
                      render={({ field }) => (
                        <div
                          style={{
                            flex: "1",
                          }}
                        >
                          <TextField
                            fullWidth
                            {...field}
                            label="Hora(s) por dia"
                            type="number"
                            inputProps={{
                              min: 1,
                              max: 24,
                            }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (Number(value) <= 24 && value.length <= 2) {
                                setValue("allocationTime", value);
                              }
                            }}
                          />
                          <div style={{ height: "20px", marginTop: "4px" }}>
                            {errors?.allocationTime && (
                              <MsgError>
                                {errors?.allocationTime?.message}
                              </MsgError>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Controller
                      name="allocationTimePerMonth"
                      control={control}
                      render={({ field }) => (
                        <div
                          style={{
                            flex: "1",
                          }}
                        >
                          <TextField
                            fullWidth
                            {...field}
                            label="Hora(s) por mês"
                            type="number"
                            inputProps={{
                              min: 1,
                            }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              setValue("allocationTimePerMonth", value);
                            }}
                          />
                          <div style={{ height: "20px", marginTop: "4px" }}>
                            {errors?.allocationTimePerMonth && (
                              <MsgError>
                                {errors?.allocationTimePerMonth?.message}
                              </MsgError>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  )}
                </div>
              </div>

              <div className={styles.totalHoursModal}>
                <h4>Total de Horas do Périodo: {totalHours}h</h4>
              </div>

              <div className={styles.projectModal}>
                {selectedOption === "Projeção" ? (
                  <Controller
                    name="projectOrUnavailability"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        fullWidth
                        freeSolo
                        options={project}
                        getOptionLabel={(option: any) =>
                          typeof option === 'string' ? option : option.value || option
                        }
                        isOptionEqualToValue={(option, value) =>
                          typeof option === 'string'
                            ? option === value
                            : option.key === value?.key || option.value === value
                        }
                        noOptionsText="Sem opções - Digite para criar novo projeto"
                        onChange={(_, newValue) => {
                          if (typeof newValue === 'string') {
                            field.onChange(newValue);
                          } else if (newValue) {
                            field.onChange(newValue.value);
                          } else {
                            field.onChange("");
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          field.onChange(newInputValue);
                        }}
                        value={field.value || ""}
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={typeof option === 'string' ? option : option.key}>
                              {typeof option === 'string' ? option : option.value}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.projectOrUnavailability}
                            label="Nome do Projeto"
                            placeholder="Digite ou selecione um projeto existente"
                            helperText={
                              errors.projectOrUnavailability?.message ||
                              "Para converter em alocação, o projeto deve estar previamente cadastrado"
                            }
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              ...params.inputProps,
                              maxLength: 200,
                            }}
                          />
                        )}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="projectOrUnavailability"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        fullWidth
                        options={selectedOption === "Projeto" ? project : unavailability}
                        getOptionLabel={(option: IKeyValue) => option.value}
                        isOptionEqualToValue={(option, value) =>
                          option.key === value?.key
                        }
                        noOptionsText="Sem opções"
                        onChange={(_, newValue) => {
                          field.onChange(newValue ? newValue.key : "");
                        }}
                        value={
                          (selectedOption === "Projeto" ? project : unavailability)?.find(
                            (option) => option.key === field.value
                          ) || null
                        }
                        renderOption={(props, option) => {
                          return (
                            <li {...props} key={option.key}>
                              {option.value}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.projectOrUnavailability}
                            label={
                              selectedOption === "Projeto"
                                ? "Projetos/Clientes"
                                : "Indisponibilidades"
                            }
                            placeholder="Selecione"
                            helperText={errors.projectOrUnavailability?.message}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        )}
                      />
                    )}
                  />
                )}
              </div>
              {selectedOption === "Projeto" && (
                <div className={styles.bilableModal}>
                  <FormControl fullWidth error={!!errors.billable}>
                    <Controller
                      name="billable"
                      control={control}
                      defaultValue={dataAllocation.isProject ? "true" : "false"}
                      render={({ field }) => (
                        <RadioGroup {...field} row>
                          <FormControlLabel
                            name="faturado"
                            value="true"
                            control={<Radio />}
                            label="Faturável"
                          />
                          <FormControlLabel
                            name="faturado"
                            value="false"
                            control={<Radio />}
                            label="Não Faturável"
                          />
                        </RadioGroup>
                      )}
                    />

                    {errors.billable && (
                      <MsgError>{errors.billable.message}</MsgError>
                    )}
                  </FormControl>
                </div>
              )}

              {selectedOption === "Projeção" && (
                <div style={{ marginBottom: "16px" }}>
                  <Controller
                    name="opportunityLink"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Link da Oportunidade"
                        fullWidth
                        placeholder="Ex: https://crm.empresa.com/oportunidade/123"
                        error={!!errors.opportunityLink}
                        helperText={
                          errors.opportunityLink?.message ||
                          "Digite o link de referência da oportunidade"
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          "aria-describedby": "opportunity-link-helper",
                          maxLength: 100,
                        }}
                        required
                      />
                    )}
                  />
                </div>
              )}

              <div className={styles.detailsModal}>
                <Controller
                  name="details"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={selectedOption === "Projeção" ? "Detalhes da Oportunidade" : "Detalhes"}
                      multiline
                      rows={3}
                      placeholder={selectedOption === "Projeção" ? "Digite os detalhes da oportunidade..." : "Insira os detalhes aqui..."}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.details}
                      helperText={errors.details?.message}
                      fullWidth
                      required={selectedOption === "Projeção"}
                    />
                  )}
                />
              </div>



              <BoxButtons>
                <div
                  style={{
                    display: "flex",
                    justifyContent: isEdit ? "flex-start" : "center",
                    alignItems: "center",
                    width: "100%",
                    gap: "8px",
                  }}
                >
                  {isEdit && (
                    <CancelButton
                      disabled={isLoading}
                      onClick={() => handleDeleteClick(othersInfoAllocation.id)}
                    >
                      Excluir
                    </CancelButton>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  {selectedOption === "Projeção" && isEdit && (
                    <Tooltip
                      title={
                        !project.find(p =>
                          p.value === watch("projectOrUnavailability") ||
                          p.key === watch("projectOrUnavailability")
                        )
                          ? "Para converter, selecione um projeto que já esteja cadastrado no sistema"
                          : "Converter esta projeção em uma alocação real"
                      }
                      placement="top"
                    >
                      <span>
                        <Button
                          variant="outlined"
                          disabled={
                            isLoading ||
                            !project.find(p =>
                              p.value === watch("projectOrUnavailability") ||
                              p.key === watch("projectOrUnavailability")
                            )
                          }
                          onClick={handleConvertToAllocation}
                          style={{
                            backgroundColor: project.find(p =>
                              p.value === watch("projectOrUnavailability") ||
                              p.key === watch("projectOrUnavailability")
                            ) ? "#1976d2" : "#ccc",
                            color: "white",
                            border: project.find(p =>
                              p.value === watch("projectOrUnavailability") ||
                              p.key === watch("projectOrUnavailability")
                            ) ? "1px solid #1976d2" : "1px solid #ccc",
                            minHeight: "36px",
                            fontSize: "0.875rem",
                            padding: "6px 16px",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                          }}
                          aria-label="Converter projeção em alocação real"
                        >
                          Converter em Alocação
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <PrimaryButton disabled={isLoading} type="submit">
                    Salvar
                  </PrimaryButton>
                </div>
              </BoxButtons>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};
